import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import {trim} from '../strings'

export const isEmpty = (str: string | undefined | null): boolean => {
  return R.isNil(str) || trim(str).length === 0
}

/** Checks if the parameter is null or empty (whitespace-only strings are considered 'empty') */
export const isPresent = <T extends string>(
  str: T | undefined | null
): str is NonNullable<T> => {
  return !isEmpty(str)
}

/** Checks if the parameter is not null nor undefined */
export const isNotNil = <T>(v: T | undefined | null): v is NonNullable<T> => {
  return RA.isNotNil(v)
}

/** Checks if the object parameter is null or {}
 * just a types wrapper around R.isEmpty
 */
export const isObjPresent = (obj: object | undefined | null): obj is object => {
  return !!obj && !R.isEmpty(obj)
}

export const undefinedIfEmpty = <T extends string>(
  str: T | undefined | null
): T | undefined => {
  return isPresent(str) ? str : undefined
}

/** Returns `defaultValue` if `maybe` is `undefined`. */
export const ifUndefined = <T>(maybe: T | undefined, defaultValue: T): T => {
  return maybe === undefined ? defaultValue : maybe
}

/** Returns `defaultValue` if `maybe` is `null`. */
export const ifNull = <T>(maybe: T | null, defaultValue: T): T => {
  return maybe === null ? defaultValue : maybe
}

/** Removes all keys whose values are `undefined` from `obj` and returns the result. */
export const purgeUndefined = <T>(obj: T): Partial<T> => {
  return R.pickBy<T, Partial<T>>(v => v !== undefined, obj)
}

/**
 * Remove all falsey values from a list. 0, '', false, undefined, and null will be removed.
 * (RA's compact isn't typed nicely for uniform lists. This wrapper is.)
 */
export const compact = <T>(
  list: Array<T | undefined | null>
): Array<NonNullable<T>> => {
  return RA.compact(list)
}

export const convertNullsToUndefined = <T>(value: T): T => {
  if (typeof value === 'object' && !!value) {
    return R.map(convertNullsToUndefined, value as any) as T
  } else if (value === null) {
    return undefined as any as T
  } else {
    return value
  }
}
